import React from 'react';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useAuth0Config } from '../../hooks/useAuth0Config';
import { Auth0Provider as Auth0ProviderSDK } from '@auth0/auth0-react';
import logger from '../../utils/logger';

interface Auth0ConfigLoaderProps {
    children: React.ReactNode;
}

const Auth0ConfigLoader: React.FC<Auth0ConfigLoaderProps> = ({ children }) => {
    const { config, loading, error } = useAuth0Config();

    // Show loading state while fetching Auth0 config
    if (loading) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="100vh"
                gap={2}
            >
                <CircularProgress size={60} />
                <Typography variant="h6" color="text.secondary">
                    Loading Auth0 Configuration...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Fetching secure authentication settings
                </Typography>
            </Box>
        );
    }

    // Show error state if Auth0 config failed to load
    if (error || !config) {
        logger.error('Auth0 configuration error:', error);
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="100vh"
                gap={2}
                p={3}
            >
                <Alert severity="error" sx={{ maxWidth: 600 }}>
                    <Typography variant="h6" gutterBottom>
                        Authentication Configuration Error
                    </Typography>
                    <Typography variant="body2" paragraph>
                        Failed to load Auth0 configuration from the backend.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Error: {error || 'Unknown configuration error'}
                    </Typography>
                </Alert>
                <Typography variant="body2" color="text.secondary">
                    Please check the backend API and try refreshing the page.
                </Typography>
            </Box>
        );
    }

    // Generate redirect URI based on current origin
    const redirectUri = window.location.origin;

    // Get the API audience from the backend configuration (environment-specific)
    const apiAudience = config.audience;
    const apiScopes = 'openid profile email offline_access read:workflows write:workflows delete:workflows read:templates write:templates delete:templates read:salts write:salts delete:salts read:storage write:storage delete:storage admin:all';

    logger.debug('🔐 AUTH0-CONFIG-LOADER: Initializing Auth0Provider with dynamic config', {
        domain: config.domain,
        clientId: config.clientId.substring(0, 8) + '...[REDACTED]',
        audience: apiAudience,
        environment: config.environment,
        redirectUri,
        scope: apiScopes,
        timestamp: new Date().toISOString()
    });

    // Additional debug logging for troubleshooting
    console.log('🔐 AUTH0-CONFIG-LOADER: Full initialization parameters:', {
        domain: config.domain,
        clientId: config.clientId,
        audience: apiAudience,
        environment: config.environment,
        cacheLocation: "localstorage",
        useRefreshTokens: true,
        authorizationParams: {
            redirect_uri: redirectUri,
            audience: apiAudience,
            scope: apiScopes
        }
    });

    // Render Auth0Provider with dynamic configuration including API audience
    // CRITICAL: useRefreshTokens MUST be true to allow silent token refresh
    // This enables the SessionTimeoutDialog to extend sessions without logout

    // 🔍 DIAGNOSTIC: Log exact Auth0 SDK initialization before rendering
    const auth0SDKConfig = {
        domain: config.domain,
        clientId: config.clientId,
        cacheLocation: "localstorage",
        useRefreshTokens: true,
        authorizationParams: {
            redirect_uri: redirectUri,
            audience: apiAudience,
            scope: apiScopes
        }
    };

    console.log('🚀 AUTH0-SDK-INIT: Exact configuration being passed to Auth0ProviderSDK:', JSON.stringify(auth0SDKConfig, null, 2));
    console.log('🚀 AUTH0-SDK-INIT: Window location:', window.location.href);
    console.log('🚀 AUTH0-SDK-INIT: Redirect URI:', redirectUri);

    return (
        <Auth0ProviderSDK
            domain={config.domain}
            clientId={config.clientId}
            cacheLocation="localstorage"
            useRefreshTokens={true}
            authorizationParams={{
                redirect_uri: redirectUri,
                audience: apiAudience,
                scope: apiScopes
            }}
            onRedirectCallback={(appState) => {
                console.log('🔄 AUTH0-SDK: Redirect callback triggered', appState);
            }}
        >
            {children}
        </Auth0ProviderSDK>
    );
};

export default Auth0ConfigLoader;
